/**
 * O que é ESTE nó — o painel lateral que abre ao clicar no grafo.
 *
 * O grafo dizia pouco: um círculo, um rótulo cortado em 24 caracteres e um tooltip com o tipo e
 * meia dúzia de vizinhos. Dava para ver que havia uma ligação e não dava para saber com QUEM — e
 * uma investigação que não sabe quem está olhando não avançou. Aqui o nó vira ficha: o cadastro
 * inteiro, os contatos, e a lista de conexões navegável.
 *
 * Três decisões:
 *
 * **A ficha é a mesma da tela de leitura** (`FichaEntidade`, montada do descriptor). Escrever uma
 * segunda versão "resumida" garantiria que um campo novo aparecesse num lugar e não no outro.
 *
 * **Navegar não fecha.** Clicar numa conexão troca o conteúdo do drawer e empilha o caminho, com
 * volta — é assim que se puxa um fio: pessoa → telefone → outra pessoa, sem perder de onde veio.
 *
 * **Recurso não é entidade.** Telefone, e-mail e endereço não têm cadastro próprio; o que importa
 * neles é justamente quem os divide, e é só isso que o drawer mostra. Fingir uma ficha para eles
 * seria inventar uma página que não existe.
 */
import { ArrowLeft, ExternalLink, Network } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FichaEntidade } from '@/components/crud/FichaEntidade';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/misc';
import { Sheet } from '@/components/ui/sheet';
import { api, type DossieCad } from '@/lib/api';
import { RECURSOS, ROTA_POR_OWNER, rotaEntidade } from '@/lib/recursos';
import { rotuloTipo } from '@/lib/recursos/rotulos';
import type { RecursoBase } from '@/lib/recursos/tipos';

/** Uma conexão do nó aberto, como o grafo já a conhece. */
export type ConexaoNo = { id: string; rotulo: string; tipo: string; via: string | null };

export type NoAberto = { id: string; rotulo: string; tipo: string; grau: number; hub: boolean };

/** Uma ARESTA aberta: a ligação em si, com as duas pontas. Clicar na linha é tão natural quanto
 * clicar no círculo, e é a ligação que costuma ser a descoberta ("por que estes dois se tocam?"). */
export type ArestaAberta = { rotulo: string; pontas: ConexaoNo[] };

/** `pessoa:019f…` → `['pessoa', '019f…']`. O valor de um recurso pode conter ':' (e-mail, endereço),
 * então corta no PRIMEIRO — o tipo é o que vem antes. */
function partir(id: string): [string, string] {
  const i = id.indexOf(':');
  return i < 0 ? [id, ''] : [id.slice(0, i), id.slice(i + 1)];
}

export function DrawerArestaGrafo({
  aresta,
  onFechar,
  onIrPara,
}: {
  aresta: ArestaAberta;
  onFechar: () => void;
  onIrPara: (id: string) => void;
}) {
  return (
    <Sheet
      aberto
      onAbrir={(v) => !v && onFechar()}
      titulo={aresta.rotulo}
      descricao="Ligação entre duas entidades — abra qualquer uma das pontas."
    >
      <div className="flex flex-col gap-2">
        {aresta.pontas.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onIrPara(c.id)}
            className="flex w-full flex-col rounded-md border border-border px-3 py-2 text-left transition-colors hover:bg-surface-hover"
          >
            <span className="truncate tipo-corpo text-text">{c.rotulo}</span>
            <span className="tipo-corpo-pequeno text-text-tertiary">
              {rotuloTipo(c.tipo)}
            </span>
          </button>
        ))}
      </div>
    </Sheet>
  );
}

export function DrawerNoGrafo({
  no,
  conexoes,
  onFechar,
  onIrPara,
  onAbrirHub,
  podeVoltar,
  onVoltar,
}: {
  no: NoAberto | null;
  conexoes: ConexaoNo[];
  onFechar: () => void;
  /** Trocar o nó aberto sem fechar o drawer — é a navegação pelo fio. */
  onIrPara: (id: string) => void;
  /** Só existe para hub: revelar no grafo os vizinhos que a régua escondeu. */
  onAbrirHub?: () => void;
  podeVoltar: boolean;
  onVoltar: () => void;
}) {
  const navegar = useNavigate();
  const [tipo, id] = no ? partir(no.id) : ['', ''];
  const chave = ROTA_POR_OWNER[tipo];
  const descritor = chave ? RECURSOS[chave] : undefined;

  const [registro, setRegistro] = useState<RecursoBase | null>(null);
  const [dossie, setDossie] = useState<DossieCad | null>(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    setRegistro(null);
    setDossie(null);
    if (!descritor || !id) return;
    let vivo = true;
    setCarregando(true);
    void Promise.all([
      api.get<RecursoBase>(`${descritor.endpoint}/${id}`),
      api.get<DossieCad>(`/cadastro/dossie/${tipo}/${id}`),
    ]).then(([reg, dos]) => {
      if (!vivo) return;
      setCarregando(false);
      if (reg.ok) setRegistro(reg.dados);
      if (dos.ok) setDossie(dos.dados);
    });
    return () => {
      vivo = false;
    };
  }, [descritor, tipo, id]);

  if (!no) return null;
  const ehEntidade = !!descritor;

  return (
    <Sheet
      aberto
      onAbrir={(v) => !v && onFechar()}
      titulo={no.rotulo}
      descricao={`${rotuloTipo(tipo)}${no.grau ? ` · ${no.grau} vínculo${no.grau === 1 ? '' : 's'}` : ''}`}
      rodape={
        <div className="flex w-full items-center justify-between gap-2">
          {podeVoltar ? (
            <Button variant="fantasma" onClick={onVoltar}>
              <ArrowLeft className="size-4" />
              Voltar
            </Button>
          ) : (
            <span />
          )}
          {ehEntidade && (
            <Button onClick={() => navegar(rotaEntidade(tipo, id))}>
              <ExternalLink className="size-4" />
              Abrir ficha completa
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* O hub esconde vizinhos de propósito (senão a tela vira emaranhado). Aqui ele diz quantos
            e oferece o botão — a régua não some com dado, só espera o operador pedir. */}
        {no.hub && onAbrirHub && (
          <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-2 p-3">
            <p className="tipo-corpo-pequeno text-text-secondary">
              Este nó tem {no.grau} vínculos — mais do que o grafo desenha de uma vez.
            </p>
            <Button variant="secundario" size="sm" onClick={onAbrirHub}>
              <Network className="size-4" />
              Mostrar no grafo
            </Button>
          </div>
        )}

        {carregando && <Spinner />}

        {ehEntidade && registro && descritor && (
          <FichaEntidade
            descritor={descritor}
            registro={registro}
            extras={[
              { rotulo: 'Telefones', valores: dossie?.telefones ?? [] },
              { rotulo: 'E-mails', valores: dossie?.emails ?? [] },
              { rotulo: 'Endereços', valores: dossie?.enderecos ?? [] },
            ]}
          />
        )}

        {!ehEntidade && (
          <div className="flex flex-col gap-1">
            <p className="tipo-rotulo">{rotuloTipo(tipo)}</p>
            <p className="tipo-dado text-text">{id || no.rotulo}</p>
            <p className="tipo-corpo-pequeno text-text-tertiary">
              {/* O valor sozinho não é achado; o achado é o compartilhamento. */}
              {conexoes.length === 1
                ? 'Usado por uma entidade — sem correlação por aqui.'
                : `Compartilhado por ${conexoes.length} entidades.`}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <p className="tipo-rotulo">Conexões neste grafo</p>
          {conexoes.length === 0 ? (
            <p className="tipo-corpo-pequeno text-text-tertiary">
              Nenhuma — este nó está isolado no recorte atual.
            </p>
          ) : (
            conexoes.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onIrPara(c.id)}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-left transition-colors hover:bg-surface-hover"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate tipo-corpo text-text">{c.rotulo}</span>
                  <span className="tipo-corpo-pequeno text-text-tertiary">
                    {rotuloTipo(c.tipo)}
                    {c.via ? ` · ${c.via}` : ''}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </Sheet>
  );
}
